"use client"

import { ArrowLeft, ArrowRight, Check, LoaderCircle, MessageSquareText } from "lucide-react"
import Link from "next/link"
import { useState } from "react"

type CreatedCase = {
  case: { id: string; reference: string; title: string }
  clarificationMessage: string
}

export function NewCaseForm() {
  const [result, setResult] = useState<CreatedCase | null>(null)
  const [error, setError] = useState("")
  const [submitting, setSubmitting] = useState(false)

  async function submit(formData: FormData) {
    setSubmitting(true)
    setError("")
    try {
      const response = await fetch("/api/demo/cases", {
        method: "POST",
        headers: {
          "content-type": "application/json",
          "idempotency-key": crypto.randomUUID(),
        },
        body: JSON.stringify({
          title: formData.get("title"),
          description: formData.get("description"),
          customerReference: formData.get("customerReference"),
          priority: formData.get("priority"),
          source: "web",
        }),
      })
      const payload = (await response.json()) as CreatedCase & {
        error?: { message?: string }
      }
      if (!response.ok) {
        throw new Error(payload.error?.message ?? "Resolto could not create the case.")
      }
      setResult(payload)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Resolto could not create the case.")
    } finally {
      setSubmitting(false)
    }
  }

  if (result) {
    return (
      <div className="intake-result" role="status" aria-live="polite">
        <div className="intake-result-heading">
          <span>
            <Check aria-hidden="true" />
          </span>
          <div>
            <p>{result.case.reference} created</p>
            <h2>{result.case.title}</h2>
          </div>
        </div>
        <div className="clarification-message">
          <span className="avatar resolto-avatar">R</span>
          <div>
            <strong>One clarification before I investigate</strong>
            <p>{result.clarificationMessage}</p>
          </div>
        </div>
        <p className="result-boundary">
          No connector check or action started. The case is waiting for this specific context.
        </p>
        <div className="button-row">
          <Link className="button button-primary" href={`/app/cases/${result.case.id}`}>
            Open case
            <ArrowRight aria-hidden="true" />
          </Link>
          <Link className="button button-secondary" href="/app/cases">
            Back to queue
          </Link>
        </div>
      </div>
    )
  }

  return (
    <form
      className="intake-form"
      onSubmit={(event) => {
        event.preventDefault()
        void submit(new FormData(event.currentTarget))
      }}
    >
      {error && (
        <div className="form-error" role="alert">
          <strong>Case not created</strong>
          <span>{error}</span>
        </div>
      )}
      <div className="intake-form-intro">
        <MessageSquareText aria-hidden="true" />
        <div>
          <h2>Describe what the employee or customer is seeing</h2>
          <p>
            Use plain language. Resolto will ask only for identifiers or evidence that is missing.
          </p>
        </div>
      </div>
      <label>
        Short issue title
        <input
          name="title"
          minLength={6}
          maxLength={140}
          required
          placeholder="Paid account still has starter access"
        />
      </label>
      <label>
        What happened?
        <textarea
          name="description"
          minLength={20}
          maxLength={5_000}
          required
          placeholder="The customer paid this morning, but their workspace still shows the starter plan. They expected Growth access."
        />
        <small>Include expected behaviour and any error the employee already has.</small>
      </label>
      <div className="field-row">
        <label>
          Customer or record reference
          <input
            name="customerReference"
            minLength={2}
            maxLength={120}
            required
            placeholder="Customer name or ID"
          />
        </label>
        <label>
          Priority
          <select name="priority" defaultValue="normal">
            <option value="low">Low</option>
            <option value="normal">Normal</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>
        </label>
      </div>
      <div className="intake-safety-note">
        <strong>Before anything runs</strong>
        <p>
          Resolto classifies the request and checks for missing context. Approved read operations,
          permissions, and limits are validated before an investigation can start.
        </p>
      </div>
      <div className="form-actions">
        <Link className="button button-secondary" href="/app/cases">
          <ArrowLeft aria-hidden="true" />
          Cancel
        </Link>
        <button className="button button-primary" type="submit" disabled={submitting}>
          {submitting ? <LoaderCircle className="spin" aria-hidden="true" /> : null}
          {submitting ? "Creating case…" : "Create case"}
        </button>
      </div>
    </form>
  )
}
